import {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import * as Yup from "yup";
import {
  addPatrimonialState,
  updatePatrimonialState,
  getPatrimonialStateByLoanApplicationId,
  getAssetsByPaatrimonialStateId,
  getLiabilitiesByPatrimonialStateId,
} from "helpers/fakebackend_helper";

import Asset from "./modals/Asset";
import Liability from "./modals/Liability";
import PatrimonialPersonalReference from "./modals/PatrimonialPersonalReference";

type Props = {
  onSuccess?: () => void;
  loanApplicationId?: string;
  isEdit?: boolean;
};

const PatrimonialStateForm = forwardRef(
  ({ onSuccess, loanApplicationId, isEdit = false }: Props, ref) => {
    const [patrimonialStateId, setPatrimonialStateId] = useState<string | undefined>(undefined);
    const [assetList, setAssetList] = useState<any[]>([]);
    const [liabilityList, setLiabilityList] = useState<any[]>([]);

    const linkifyEntity = (entityName: string, id: number) => {
      return `/${entityName}s/${id}`;
    };

    const { showLoader, hideLoader } = useLoader();

    // Función para calcular totales
    const calculateTotals = () => {
      const assetTotal = assetList.reduce((sum, asset) => sum + (asset.value || 0), 0);
      const liabilityTotal = liabilityList.reduce((sum, liability) => sum + (liability.value || 0), 0);
      const netWorth = assetTotal - liabilityTotal;
      // La liquidez podría ser un porcentaje o una fórmula específica
      // Por ahora la calculamos como el 10% del patrimonio neto (ajusta según tu lógica)
      const liquidity = netWorth * 0.3;

      return { assetTotal, liabilityTotal, netWorth, liquidity };
    };

    // Función para actualizar los valores calculados en el formulario
    const updateCalculatedValues = () => {
      const { assetTotal, liabilityTotal, netWorth, liquidity } = calculateTotals();

      validation.setFieldValue("assetTotal", assetTotal, false);
      validation.setFieldValue("liabilityTotal", liabilityTotal, false);
      validation.setFieldValue("netWorth", netWorth, false);
      validation.setFieldValue("liquidity", liquidity, false);
    };

    // Función para obtener assets y liabilities
    const fetchAssetsAndLiabilities = async () => {
      if (!patrimonialStateId) return;

      try {
        const [assetsResponse, liabilitiesResponse] = await Promise.all([
          getAssetsByPaatrimonialStateId({ patrimonialStateId }),
          getLiabilitiesByPatrimonialStateId({ patrimonialStateId })
        ]);

        const assets = assetsResponse.data._embedded?.assets || [];
        const liabilities = liabilitiesResponse.data._embedded?.liabilities || [];

        setAssetList(assets);
        setLiabilityList(liabilities);
      } catch (error) {
        console.error("Error fetching assets and liabilities:", error);
      }
    };

    // Efecto para recalcular cuando cambien los assets o liabilities
    useEffect(() => {
      updateCalculatedValues();
    }, [assetList, liabilityList]);

    // Efecto para obtener datos cuando cambie el patrimonialStateId
    useEffect(() => {
      if (patrimonialStateId) {
        fetchAssetsAndLiabilities();
      }
    }, [patrimonialStateId]);

    const validation = useFormik({
      enableReinitialize: true,
      initialValues: {
        loanApplication: "",
        assetTotal: 0,
        liabilityTotal: 0,
        netWorth: 0,
        liquidity: 0,
        description: "",
      },
      validationSchema: Yup.object({
        loanApplication: Yup.string().required("La solicitud es requerida"),
      }),
      onSubmit: async (values: any) => {
        showLoader();
        try {
          // Recalcular antes de enviar
          const { assetTotal, liabilityTotal, netWorth, liquidity } = calculateTotals();
          
          const payload = {
            loanApplication: isEdit ? linkifyEntity("loanApplication", Number(values.loanApplication)) : values.loanApplication,
            assetTotal,
            liabilityTotal,
            netWorth,
            liquidity,
            description: values.description,
          };

          if (isEdit && patrimonialStateId) {
            await updatePatrimonialState(patrimonialStateId, payload);
            toast.success("Estado patrimonial actualizado correctamente");
            onSuccess?.();
          } else if (patrimonialStateId) {
            await updatePatrimonialState(patrimonialStateId, payload);
            toast.success("Estado Patrimonial actualizado correctamente");
            onSuccess?.();
          }
        } catch (error) {
          toast.error("Error guardando Patrimonial State");
        } finally {
          hideLoader();
        }
      },
    });

    useImperativeHandle(ref, () => ({
      submitForm: () => validation.handleSubmit(),
      resetForm: () => {
        validation.resetForm();
        setPatrimonialStateId(undefined);
        setAssetList([]);
        setLiabilityList([]);
      },
      refreshTotals: () => {
        fetchAssetsAndLiabilities();
      },
    }));

    const createPatrimonialState = async () => {
      if (isEdit || !loanApplicationId) return;

      showLoader();
      try {
        const payload = {
          loanApplication: linkifyEntity("loanApplication", Number(loanApplicationId)),
          assetTotal: 0,
          liabilityTotal: 0,
          netWorth: 0,
          liquidity: 0,
          description: "",
        };

        const response = await addPatrimonialState(payload);
        const id = response.data?.patrimonialStateId;

        toast.success("Patrimonial State creado automáticamente");
        setPatrimonialStateId(id);
      } catch (error) {
        toast.error("Error creando Patrimonial State automáticamente");
      } finally {
        hideLoader();
      }
    };

    useEffect(() => {
      if (loanApplicationId) {
        const loanLink = linkifyEntity("loanApplication", Number(loanApplicationId));
        validation.setFieldValue("loanApplication", loanLink, true);

        if (isEdit) {
          fetchPatrimonialStateData(loanApplicationId);
        } else {
          createPatrimonialState();
        }
      }
    }, [loanApplicationId, isEdit]);

    /**
     * Fetch Patrimonial State Data if Editing
     */
    const fetchPatrimonialStateData = async (id: string) => {
      showLoader();
      try {
        if (!id) return;

        const params: any = { loanApplicationId: id };
        const response = await getPatrimonialStateByLoanApplicationId(params);
        const data = response.data;

        if (data) {
          setPatrimonialStateId(data.patrimonialStateId);
          validation.setValues({
            loanApplication: data.loanApplication.loanApplicationId,
            assetTotal: data.assetTotal || 0,
            liabilityTotal: data.liabilityTotal || 0,
            liquidity: data.liquidity || 0,
            netWorth: data.netWorth || 0,
            description: data.description || "",
          });
        }
      } catch (error) {
        toast.error("Error al obtener el Patrimonial State");
      } finally {
        hideLoader();
      }
    };

    return (
      <div>
        <Asset 
          patrimonialStateId={patrimonialStateId} 
          onSuccess={() => {
            fetchAssetsAndLiabilities();
          }}
        />

        <Liability 
          patrimonialStateId={patrimonialStateId} 
          onSuccess={() => {
            fetchAssetsAndLiabilities();
          }}
        />

        <PatrimonialPersonalReference patrimonialStateId={patrimonialStateId} />

        <form onSubmit={validation.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="assetTotal" className="inline-block mb-2 text-base font-medium">
                Total Ingresos
              </label>
              <input
                type="number"
                name="assetTotal"
                value={validation.values.assetTotal}
                readOnly
                className="form-input bg-gray-100 cursor-not-allowed"
                title="Calculado automáticamente basado en los ingresos agregados"
              />
              <small className="text-gray-500">Calculado automáticamente</small>
            </div>

            <div>
              <label htmlFor="liabilityTotal" className="inline-block mb-2 text-base font-medium">
                Total Gastos
              </label>
              <input
                type="number"
                name="liabilityTotal"
                value={validation.values.liabilityTotal}
                readOnly
                className="form-input bg-gray-100 cursor-not-allowed"
                title="Calculado automáticamente basado en los gastos agregados"
              />
              <small className="text-gray-500">Calculado automáticamente</small>
            </div>

            <div>
              <label htmlFor="netWorth" className="inline-block mb-2 text-base font-medium">
                Patrimonio Neto
              </label>
              <input
                type="number"
                name="netWorth"
                readOnly
                value={validation.values.netWorth}
                className="form-input bg-gray-100 cursor-not-allowed"
                title="Total Ingresos - Total Gastos"
              />
              <small className="text-gray-500">Ingresos - Gastos</small>
            </div>

            <div>
              <label htmlFor="liquidity" className="inline-block mb-2 text-base font-medium">
                Liquidez
              </label>
              <input
                type="number"
                name="liquidity"
                readOnly
                value={validation.values.liquidity}
                className="form-input bg-gray-100 cursor-not-allowed"
                title="Calculado como 10% del patrimonio neto"
              />
              <small className="text-gray-500">30% del patrimonio neto</small>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="inline-block mb-2 text-base font-medium">
                Comentarios
              </label>
              <textarea
                name="description"
                className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                value={validation.values.description}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
              />
            </div>
          </div>

          {/* Botón para recalcular manualmente si es necesario */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => fetchAssetsAndLiabilities()}
              className="btn bg-blue-500 text-white text-sm"
            >
              Recalcular Totales
            </button>
          </div>
        </form>
      </div>
    );
  }
);

export default PatrimonialStateForm;